import React, { useState, useEffect, useRef, useMemo } from "react";
import { IconPlus, IconFileText, IconUpload, IconSearch, IconDownload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { ApiConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { ApiUtils, HumpUtils, StorageUtils } from "utils";
import { useUnitList, useDepartmentList, useRole } from "hooks";

const Heading = ({
  form,
  activeFunctionOptions,
  checkedUsers,
  pagination,
  funcExportExcel,
  onChangeSearchInput,
  onChangeActiveFunction,
  onClickActiveButton,
  onClickInactiveButton,
  onClickDeleteButton,
  onClickImportUser,
  onClickCreateUser,
  handleChangeUnitDropdown,
  handleChangeDepartmentDropdown,
  handleChangePositionDropdown,
  onClearAllChecked,
}) => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);

  const { isServiceAdmin } = useRole();
  const { totalUnitList } = useUnitList();
  const { departmentList, handleToggleShowDepartment } = useDepartmentList();
  const isExportDisabled = !pagination || pagination.total_users === 0;

  const { data: positionsData } = useQuery({
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getPositionList, "heading_filter"],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);
      const payload = {
        branchId,
        paging: 1,
        limit: 10000,
        filter: JSON.stringify({}),
        asc: 0,
        offset: 0,
        orderBy: "created",
      };
      const response = await ApiUtils.createApiWithToken(false).get(
        ApiConstant.GET_LIST_POSITIONS,
        HumpUtils.decamelizeKeys(payload),
      );
      return HumpUtils.camelizeKeys(response.data);
    },
  });

  const positionOptions = useMemo(() => {
    let rawList = positionsData?.data?.data;
    if (!Array.isArray(rawList)) {
      rawList = positionsData?.data || [];
    }
    if (!Array.isArray(rawList)) return [];

    let filtered = rawList;
    if (form.unitId && String(form.unitId) !== "0" && String(form.unitId) !== "-1") {
      filtered = filtered.filter(p => String(p.unitId) === String(form.unitId));
    }
    if (form.departmentId && String(form.departmentId) !== "0") {
      filtered = filtered.filter(p => String(p.departmentId) === String(form.departmentId));
    }

    const uniqueNames = Array.from(
      new Set(filtered.map(p => p.positionName).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b, "vi"));

    return uniqueNames.map(name => ({
      value: `pos-name:${name}`,
      label: name,
    }));
  }, [positionsData, form.unitId, form.departmentId]);

  const [unitList, setUnitList] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [filterDepartmentList, setFilterDepartmentList] = useState([]);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);

  const handleClickAddUser = () => {
    setShowAddMenu(prev => !prev);
  };

  const handleCloseMenuOpts = () => {
    setShowAddMenu(false);
  };

  const onExport = async () => {
    const response = await funcExportExcel();
    if (response) {
      const link = document.createElement("a");
      link.href = response;
      link.download = "Danh_sach_nguoi_su_dung.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    if (totalUnitList) {
      setUnitList(
        [
          { unitId: 0, unitName: getLabel(LangConstant.TXT_FILTER_ALL) },
          { unitId: -1, unitName: getLabel(LangConstant.TXT_FILTER_OTHER) },
        ].concat(
          [...totalUnitList].sort((a, b) => {
            return (a.unitName || "").localeCompare(b.unitName || "", "vi");
          }),
        ),
      );
    }
  }, [totalUnitList]);

  useEffect(() => {
    if (departmentList && Object.keys(departmentList).includes(selectedUnitId)) {
      setFilterDepartmentList(
        [{ departmentId: 0, departmentName: getLabel(LangConstant.TXT_FILTER_ALL) }].concat(
          [...departmentList[selectedUnitId]].sort((a, b) => {
            return (a.departmentName || "").localeCompare(b.departmentName || "", "vi");
          }),
        ),
      );
    } else {
      setFilterDepartmentList([{ departmentId: 0, departmentName: getLabel(LangConstant.TXT_FILTER_ALL) }]);
    }
  }, [departmentList, selectedUnitId]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    if (showAddMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAddMenu]);

  const selectedCount = checkedUsers.data.length;

  const canActivate = useMemo(() => {
    if (selectedCount === 0) return false;
    // FR-WA-03: KHÔNG cho phép kích hoạt khi có bất kỳ bản ghi PENDING (state = 3) nào được chọn,
    // và chỉ hiển thị nút khi có ít nhất một bản ghi hợp lệ cần kích hoạt (không phải PENDING).
    const hasPending = checkedUsers.data.some(u => u.state === SystemConstant.STATE.PENDING);
    if (hasPending) return false;
    return checkedUsers.data.some(u => {
      const activeSt = u.branchAccountStatus !== undefined ? u.branchAccountStatus : u.status;
      const joinSt = u.branchAccountState !== undefined ? u.branchAccountState : u.state;
      return activeSt === 0 || joinSt === 0 || u.state === 0;
    });
  }, [checkedUsers.data, selectedCount]);

  const canDisable = useMemo(() => {
    if (selectedCount === 0) return false;
    // Hiển thị nút "Vô hiệu hóa" khi có user đang active (branchAccountStatus = 1)
    // và không phải PENDING (state = 3), bao gồm cả "Ngưng hoạt động" (state = 5)
    return checkedUsers.data.some(u => {
      const activeSt = u.branchAccountStatus !== undefined ? u.branchAccountStatus : u.status;
      return activeSt === SystemConstant.BRANCH_STATUS.ACTIVE && u.state !== SystemConstant.STATE.PENDING;
    });
  }, [checkedUsers.data, selectedCount]);

  const canDelete = useMemo(() => {
    return selectedCount > 0;
  }, [selectedCount]);

  const onChangeUnit = e => {
    setSelectedUnitId(e.target.value);
    handleChangeUnitDropdown(e);
    handleToggleShowDepartment(e.target.value);
  };

  return (
    <div className="d-flex flex-column w-100 gap-3 mb-3">
      {/* ROW 1: Prominent Search Bar on Left + Primary Actions on Right */}
      <div className="d-flex flex-row align-items-center justify-content-between gap-3 flex-wrap">
        {/* Prominent Search Input */}
        <div className="flex-grow-1" style={{ minWidth: 260, maxWidth: 450 }}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              defaultValue={form.search}
              onChange={onChangeSearchInput}
              placeholder={getLabel(LangConstant.P_ADD_NEW_USER)}
            />
            <span className="input-group-text">
              <IconSearch size={18} />
            </span>
          </div>
        </div>

        {/* Primary Page Action Buttons & Contextual Action Buttons */}
        <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
          {/* Action Buttons: ONLY shown when at least one user is checked */}
          {selectedCount > 0 && (
            <div className="d-flex align-items-center gap-1 me-2 pe-2 border-end">
              {canActivate && (
                <button
                  onClick={onClickActiveButton}
                  className="btn btn-success"
                  style={{ display: !isServiceAdmin ? "block" : "none", width: "fit-content", minWidth: "unset" }}
                >
                  {getLabel(LangConstant.L_ACTIVE_ACTION)}
                </button>
              )}
              {canDisable && (
                <button
                  onClick={onClickInactiveButton}
                  className="btn btn-danger"
                  style={{ display: !isServiceAdmin ? "block" : "none", width: "fit-content", minWidth: "unset" }}
                >
                  {getLabel(LangConstant.L_INACTIVE_ACTION)}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={onClickDeleteButton}
                  className="btn btn-outline-danger"
                  style={{ width: "fit-content", minWidth: "unset" }}
                >
                  {getLabel(LangConstant.TXT_DELETE)}
                </button>
              )}
            </div>
          )}

          <button
            disabled={isExportDisabled}
            onClick={onExport}
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-1"
            style={{ width: "fit-content", minWidth: "unset" }}
          >
            <IconDownload size={16} />
            {getLabel(LangConstant.TXT_EXPORT_USER_LIST)}
          </button>
          <div style={{ position: "relative" }} ref={addMenuRef}>
            <button onClick={handleClickAddUser} className="btn btn-primary d-inline-flex align-items-center gap-1" style={{ width: "fit-content", minWidth: "unset" }}>
              <IconPlus size={16} />
              {getLabel("TXT_ADD_NEW")}
            </button>

            {showAddMenu && (
              <AddUserOptions
                onClose={handleCloseMenuOpts}
                onClickCreateUser={onClickCreateUser}
                onClickImportUser={onClickImportUser}
              />
            )}
          </div>
        </div>
      </div>

      {/* ROW 2: Filter Toolbar - 5 Harmonious Equal Dropdown Columns */}
      <div className="row g-2 align-items-end">
        <div className="col-md col-sm-6 col-12" style={{ display: !isServiceAdmin ? "block" : "none" }}>
          <label
            className="form-label small text-muted mb-1 text-nowrap text-truncate d-block"
            title={getLabel(LangConstant.L_ACTIVE_FUNCTION)}
          >
            {getLabel(LangConstant.L_ACTIVE_FUNCTION)}
          </label>
          <select
            className="form-select"
            value={form.activeFunction}
            onChange={onChangeActiveFunction}
          >
            {activeFunctionOptions.map(option => (
              <option key={option.value} value={option.value}>
                {getLabel(option.label)}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md col-sm-6 col-12">
          <label className="form-label small text-muted mb-1 text-nowrap text-truncate d-block" title={getLabel(LangConstant.TXT_UNIT)}>
            {getLabel(LangConstant.TXT_UNIT)}
          </label>
          <select
            className="form-select"
            value={form.unitId}
            onChange={e => onChangeUnit(e)}
          >
            {unitList.map(data => (
              <option key={data.unitId} value={data.unitId}>
                {data.unitName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md col-sm-6 col-12">
          <label className="form-label small text-muted mb-1 text-nowrap text-truncate d-block" title={getLabel(LangConstant.TXT_DEPARTMENT)}>
            {getLabel(LangConstant.TXT_DEPARTMENT)}
          </label>
          <select
            className="form-select"
            value={form.departmentId}
            onChange={handleChangeDepartmentDropdown}
          >
            {filterDepartmentList.map(data => (
              <option key={data.departmentId} value={data.departmentId}>
                {data.departmentName}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md col-sm-6 col-12">
          <label className="form-label small text-muted mb-1 text-nowrap text-truncate d-block" title={getLabel("TXT_POSITION")}>
            {getLabel("TXT_POSITION")}
          </label>
          <select
            className="form-select"
            value={form.positionId || 0}
            onChange={handleChangePositionDropdown}
          >
            <option value={0}>{getLabel(LangConstant.TXT_FILTER_ALL)}</option>
            {positionOptions.map(pos => (
              <option key={pos.value} value={pos.value}>
                {pos.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default Heading;

const AddUserOptions = ({ onClose, onClickCreateUser, onClickImportUser }) => {
  const { t: getLabel } = useTranslation([LangConstant.NS_COMMON, LangConstant.NS_LOGIN]);

  const addUserOptions = [
    { icon: <IconFileText size={20} />, name: getLabel("TXT_FILL_FORM"), onClick: onClickCreateUser },
    { icon: <IconUpload size={20} />, name: getLabel("TXT_UPLOAD_FILE"), onClick: onClickImportUser },
  ];

  return (
    <div className="dropdown-menu show" style={{ position: "absolute", top: "100%", right: 0, zIndex: 1050, minWidth: 200 }}>
      {addUserOptions.map((options, index) => (
        <button
          key={index}
          className="dropdown-item d-flex align-items-center gap-2"
          onClick={() => {
            options.onClick();
            onClose();
          }}
        >
          {options.icon}
          <span>{options.name}</span>
        </button>
      ))}
    </div>
  );
};
